import { toast } from '@livable/saas-react-components';
import { useEffect, useRef } from 'react';
import { useStore } from 'src/stores';
const POLL_INTERVAL_MS = 5000;

/**
 * Caps, the same as the bill details view's AP push poll and the uploaded-bill poller use (LB-5944).
 *
 * The failure cap holds whatever started the poll: each read is quiet, so an outage polled without a cap
 * would ask forever, and it ends with one notice rather than a toast per read. The poll cap holds only while a push
 * alone keeps the poll going. A pending push can be stuck for good, since a Kafka publish that failed
 * leaves a row nothing will ever finish, while the service settles a stuck extraction itself and a bulk
 * upload can take longer than the cap to read.
 */
const MAX_POLLS = 120;
const MAX_FAILED_POLLS = 3;

/**
 * What the table says when failures end the poll (LB-5982). Once, because each read before it was quiet;
 * and said at all, because the table stops changing here, and a push still pending would otherwise look
 * stuck with nothing to say why. Refresh starts the poll again.
 */
const STOPPED_UPDATING = {
  title: 'The bills list stopped updating',
  body: ["We couldn't reach the server, so statuses on this page may be out of date. Select Refresh to try again."]
};

/**
 * Re-reads the Bills list while anything on it is still on its way. Extraction keeps moving bills along in
 * the background, and so does an AP push once it is queued: AppFolio finishes that one through Kafka,
 * seconds after the trigger answered.
 *
 * It rests while the details view is open (`isPaused`): that view reads its own bill, and the whole list
 * is worth reading again once, on the way out.
 *
 * The caps count afresh on new work only: a push turning pending, more bills extracting than before (an
 * upload, a retry), the batch scope changing, a bill's details closing, or `restarts` moving on (a Refresh,
 * a push or an attach from the table). Never on work finishing: a push landing elsewhere on the list must
 * not hand a stuck one a fresh allowance, nor an extraction settling mid-outage clear the failures.
 */
export const useBillsListPoll = _ref => {
  let isPaused = _ref.isPaused,
    batchId = _ref.batchId,
    restarts = _ref.restarts;
  const _useStore = useStore(),
    companyBills = _useStore.companyBills;
  const isExtracting = companyBills.hasBillsInProgress;
  const extractingCount = companyBills.billsInProgressCount;
  const unsettledPushes = companyBills.unsettledApPushBillIds.join(',');
  const isInFlight = (isExtracting || unsettledPushes !== '') && !isPaused;

  /** Kept across the effect re-running, which work finishing makes it do. */
  const polls = useRef(0);
  const failedPolls = useRef(0);
  const known = useRef({
    pushes: [],
    extractingCount: 0
  });
  useEffect(() => {
    const pushes = unsettledPushes ? unsettledPushes.split(',') : [];
    const isNewWork = pushes.some(billId => !known.current.pushes.includes(billId)) || extractingCount > known.current.extractingCount;
    known.current = {
      pushes,
      extractingCount
    };
    if (!isNewWork) return;
    polls.current = 0;
    failedPolls.current = 0;
  }, [unsettledPushes, extractingCount]);
  useEffect(() => {
    polls.current = 0;
    failedPolls.current = 0;
  }, [batchId, restarts, isPaused]);

  /**
   * Refreshes are chained instead of scheduled on a fixed interval: `run` cancels the request that is
   * still in flight, so a tick landing mid-pass would abort it. The gap is measured from the end of one
   * pass to the next.
   */
  useEffect(() => {
    const hasGivenUp = () => failedPolls.current >= MAX_FAILED_POLLS || !isExtracting && polls.current >= MAX_POLLS;
    if (!isInFlight || hasGivenUp()) return undefined;
    let isActive = true;
    let timeoutId = null;
    const poll = async () => {
      // Counted only while a push alone keeps the poll going; extraction is not capped.
      if (!isExtracting) polls.current += 1;

      // A list read in flight now is someone's own -- a page change, a filter, a Refresh -- because the poll
      // waits for its own. Asking again would cancel it, and a quiet read failing in its place would take its
      // error toast with it, so this pass leaves it be and the next one asks.
      if (companyBills.getCompanyBillsAsync.inProgress) {
        if (!hasGivenUp()) timeoutId = setTimeout(poll, POLL_INTERVAL_MS);
        return;
      }
      let outcome = 'failed';
      try {
        // Awaited, because `run` cancels whatever is still in flight on the same async action: a list
        // read slower than the gap would be aborted by the next tick, and on a slow connection the table
        // would then never refresh at all while a bill was still moving.
        const resp = await companyBills.refetchCurrent({
          quiet: true
        });
        if (resp !== null && resp !== void 0 && resp.isSuccessful) {
          outcome = 'read';
        } else if (companyBills.nothingToAsk || !resp && !companyBills.getCompanyBillsAsync.isError) {
          // No request answered at all: a view with nothing to ask for, or a read cancelled by a newer
          // one. Neither says whether the service is answering, so the streak stands as it was. An
          // answer that came back unsuccessful is a failed read, whatever the async model recorded.
          outcome = 'skipped';
        }
        // A bill settling out of Processing is a count moving, so the tiles follow the list.
        await companyBills.getCompanyBillsSummaryAsync.run(undefined, []);
      } catch (_unused) {
        // A read that never reached the service throws rather than resolving. It failed all the same, and
        // left uncaught it ended the poll for good.
      }
      if (!isActive) return;
      if (outcome === 'read') failedPolls.current = 0;
      if (outcome === 'failed') failedPolls.current += 1;
      if (failedPolls.current === MAX_FAILED_POLLS) toast('warning', STOPPED_UPDATING);
      if (hasGivenUp()) return;
      timeoutId = setTimeout(poll, POLL_INTERVAL_MS);
    };
    timeoutId = setTimeout(poll, POLL_INTERVAL_MS);
    return () => {
      isActive = false;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [isInFlight, isExtracting, unsettledPushes, extractingCount, batchId, restarts, companyBills]);
};